import { ArrowLeft, Info } from "lucide-react"
import { DatasetUploadsPage, type DatasetUploadsPageProps } from "@/components/dataset-uploads/DatasetUploadsPage"
import { timeseriesConfig, MONTHS } from "@/lib/datasetRegistry"

// Thin config module — driven by `timeseriesConfig` (inline-history mode: no
// tracked panel, no monthly alert, always-visible header buttons). The only
// page-specific JSX is the Data-Integrity deeplink banner (headerBanner slot)
// and the region-list dropzone line. Controller renders "admin/timeseries/Uploads".

type TimeseriesProps = Omit<DatasetUploadsPageProps, "config"> & {
  integrity_return_to?: string | null
  integrity_outcome?: string | null
}

export default function AdminTimeseriesUploads({ integrity_return_to, integrity_outcome, ...props }: TimeseriesProps) {
  return (
    <DatasetUploadsPage
      config={timeseriesConfig}
      headerBanner={
        integrity_return_to ? (
          <IntegrityBanner
            region={props.filters.region ?? null}
            year={props.filters.year ?? null}
            month={props.filters.month ?? null}
            outcome={integrity_outcome}
            returnTo={integrity_return_to}
          />
        ) : null
      }
      dropzoneDetail={
        <p className="mt-1 text-xs text-ink-muted">
          Region: Jakarta 1 · RegBar · RegCen · RegTim · Wipro Unza Indonesia ECOM
        </p>
      }
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// IntegrityBanner — shown when navigating from a Data Integrity mismatch row
// ---------------------------------------------------------------------------

function IntegrityBanner({
  region,
  year,
  month,
  outcome,
  returnTo,
}: {
  region: string | null
  year: string | null
  month: string | null
  outcome: string | null | undefined
  returnTo: string
}) {
  const periodLabel = [
    month ? MONTHS[parseInt(month, 10)] : null,
    year,
  ].filter(Boolean).join(" ") || null

  const isExtraInDb = outcome === "extra_in_db"

  return (
    <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 text-sm">
        {isExtraInDb ? (
          <p className="text-ink-display">
            Verifikasi: data untuk Region{" "}
            {region && <strong>{region}</strong>}
            {periodLabel && <> periode <strong>{periodLabel}</strong></>}{" "}
            ada di database tapi tidak ada di SoT. Pertimbangkan apakah upload
            Timeseries-nya valid atau perlu diperbaiki.
          </p>
        ) : (
          <p className="text-ink-display">
            Anda sedang memperbaiki Region{" "}
            {region && <strong>{region}</strong>}
            {periodLabel && <> untuk periode <strong>{periodLabel}</strong></>}.{" "}
            Upload file Timeseries yang sesuai. Setelah selesai, kembali ke Data
            Integrity dan jalankan ulang check.
          </p>
        )}
        <a
          href={returnTo}
          className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
        >
          <ArrowLeft className="h-3 w-3" />
          Kembali ke Data Integrity
        </a>
      </div>
    </div>
  )
}
